import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthResponse } from '@rw/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useT } from '../../i18n';
import { isInAppBrowser } from '../../lib/in-app-browser';
import { GOOGLE_BUTTON_STYLE, SOCIAL_BUTTON_HEIGHT, loadScript, useGoogleButton } from './google';

interface Providers {
  google: { clientId: string } | null;
  facebook: { appId: string } | null;
  strava?: { enabled: boolean } | null;
}

type FacebookSdk = {
  init(o: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }): void;
  login(cb: (r: { authResponse?: { accessToken: string } }) => void, o: { scope: string }): void;
};
declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

/** Google / Facebook sign-in; only rendered for providers the server has configured. */
export function SocialButtons({ onSignedIn, onError, mode = 'login' }: { onSignedIn: () => void; onError: (m: string) => void; mode?: 'login' | 'register' }) {
  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api<Providers>('/auth/providers'),
    staleTime: 10 * 60_000,
  });
  const setSession = useAuth((s) => s.setSession);
  const qc = useQueryClient();
  const googleEl = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const t = useT();
  const p = providers.data;

  const finish = async (path: string, body: unknown) => {
    setBusy(true);
    try {
      const res = await api<AuthResponse>(path, { body });
      qc.clear();
      setSession(res.token, res.user);
      onSignedIn();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : t('Sign-in failed'));
    } finally {
      setBusy(false);
    }
  };

  const inApp = isInAppBrowser();
  useGoogleButton(
    googleEl,
    inApp ? null : p?.google?.clientId,
    (credential) => void finish('/auth/google', { credential }),
    () => onError(t('Google sign-in is unavailable right now')),
    mode === 'register' ? 'signup_with' : 'signin_with',
  );

  useEffect(() => {
    if (!p?.facebook) return;
    window.fbAsyncInit = () => window.FB!.init({ appId: p.facebook!.appId, version: 'v19.0', cookie: false, xfbml: false });
    loadScript('https://connect.facebook.net/en_US/sdk.js').catch(() => undefined);
  }, [p?.facebook]);

  if (!p || (!p.google && !p.facebook && !p.strava)) return null;

  const strava = async () => {
    setBusy(true);
    try {
      const { url } = await api<{ url: string }>('/auth/strava/authorize', { method: 'POST' });
      window.location.assign(url);
    } catch (e) {
      setBusy(false);
      onError(e instanceof ApiError ? e.message : t('Strava sign-in is unavailable right now'));
    }
  };

  return (
    <div className="mt-6" data-testid="social-login">
      <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-widest text-ink-500">
        <span className="h-px flex-1 bg-ink-700" /> {t('or')} <span className="h-px flex-1 bg-ink-700" />
      </div>
      <div className="space-y-3">
        {p.strava ? (
          <button
            type="button"
            disabled={busy}
            onClick={strava}
            aria-label="Sign in with Strava"
            data-testid="strava-login"
            className="flex w-full justify-center disabled:opacity-50"
          >
            {/* Official "Connect with Strava" button (Strava brand guidelines). */}
            <img src="/brand/strava/btn_strava_connect_with_orange.svg" alt="Connect with Strava" style={{ height: SOCIAL_BUTTON_HEIGHT }} className="w-auto" />
          </button>
        ) : null}
        {p.google && inApp ? <OpenInBrowserNote /> : null}
        {p.google && !inApp ? <div ref={googleEl} className="flex justify-center" style={GOOGLE_BUTTON_STYLE} data-testid="google-login" /> : null}
        {p.facebook ? (
          <button
            type="button"
            disabled={busy}
            data-testid="facebook-login"
            onClick={() => {
              if (!window.FB) return onError(t('Facebook sign-in is still loading, try again'));
              window.FB.login(
                (r) => (r.authResponse ? void finish('/auth/facebook', { accessToken: r.authResponse.accessToken }) : undefined),
                { scope: 'public_profile,email' },
              );
            }}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#1877F2] font-semibold text-white disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
              <path d="M24 12a12 12 0 1 0-13.9 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.4l-.5 3.5h-2.9v8.4A12 12 0 0 0 24 12z" />
            </svg>
            {t('Continue with Facebook')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Shown instead of Google's button inside Zalo/Messenger/Instagram, where Google refuses to sign in. */
export function OpenInBrowserNote() {
  const t = useT();
  return (
    <p
      className="rounded-xl border border-ink-700 bg-ink-850 px-4 py-3 text-center text-xs text-ink-300"
      data-testid="google-open-in-browser"
    >
      {t(
        'Google sign-in doesn’t work inside Zalo or Messenger. Open this page in Safari or Chrome (⋯ → Open in browser).',
      )}{' '}
      <Link to="/guide#cai-dat" className="font-semibold text-volt underline">
        {t('Show me how')}
      </Link>
    </p>
  );
}
